import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  EncounterOwnerSchema,
  ConfirmationDialog,
  Popover,
  PopoverContent,
  PopoverTrigger,
  toast,
} from '../../../../core'
import { useApiOrgSlug } from '../../../utils/orgSlug'
import {
  useApiMutation,
  useApiQuery,
  useApiQueryClient,
} from '../../../utils/reactQuery'
import type { components } from '@joyful/openapi/types.gen'
import { extractErrorMessage } from '@joyful/utils'
import type { ReactElement, ReactNode } from 'react'
import { useState } from 'react'

type EncounterOwner = components['schemas']['EncounterOwner']

export interface EncounterOwnerOverridePopoverProps {
  children: ReactElement
  encounterId: string
  fallback: ReactNode
  onOpenChange?: (isOpen: boolean) => void
  owner: EncounterOwner
}

export function EncounterOwnerOverridePopover({
  children,
  encounterId,
  fallback,
  onOpenChange,
  owner,
}: EncounterOwnerOverridePopoverProps) {
  const [isOpen, setOpen] = useState(false)
  const [pendingOwner, setPendingOwner] = useState<EncounterOwner | null>(null)
  const orgSlug = useApiOrgSlug()
  const { invalidateApiQueries } = useApiQueryClient()
  const path = { encounter_id: encounterId, org_slug: orgSlug }
  const ownerOptionsQuery = useApiQuery(
    'get',
    '/{org_slug}/encounters/{encounter_id}/owner-options',
    { params: { path } },
  )
  const setOwner = useApiMutation(
    'post',
    '/{org_slug}/encounters/{encounter_id}/owner',
    {
      onError: (error) => {
        toast.error(`Failed to set owner: ${extractErrorMessage(error)}`)
      },
      onSuccess: async () => {
        await invalidateApiQueries(
          '/{org_slug}/encounters',
          '/{org_slug}/encounters/items',
          '/{org_slug}/work_blocks',
          {
            options: { params: { path } },
            path: '/{org_slug}/encounters/{encounter_id}',
          },
          {
            options: { params: { path } },
            path: '/{org_slug}/encounters/{encounter_id}/owner-options',
          },
          {
            options: { params: { path } },
            path: '/{org_slug}/encounters/{encounter_id}/timeline',
          },
        )
      },
    },
  )

  const allowedOwners = ownerOptionsQuery.data?.allowed_owners ?? []
  const hasOtherAllowedOwner = allowedOwners.some((allowedOwner) => {
    return allowedOwner !== owner
  })
  if (!hasOtherAllowedOwner) {
    return fallback
  }

  return (
    <ConfirmationDialog
      confirmationDescription={
        pendingOwner
          ? `${EncounterOwnerSchema.meta(pendingOwner).title} becomes responsible for the next move on this encounter.`
          : undefined
      }
      confirmationTitle={
        pendingOwner
          ? `Set owner to ${EncounterOwnerSchema.meta(pendingOwner).title}?`
          : undefined
      }
      confirmButtonText={`Set owner`}
      open={pendingOwner !== null}
      performAction={() => {
        if (pendingOwner) {
          setOwner.mutate({
            body: { owner: pendingOwner },
            params: { path },
          })
        }
      }}
      onOpenChange={(isDialogOpen) => {
        if (!isDialogOpen) {
          setPendingOwner(null)
        }
      }}
    >
      <Popover
        open={isOpen}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen)
          onOpenChange?.(nextOpen)
        }}
      >
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent align="start" padding="none" side="bottom">
          <Command>
            <CommandList>
              <CommandGroup heading={`Set encounter owner`}>
                {allowedOwners.map((allowedOwner) => {
                  const metadata = EncounterOwnerSchema.meta(allowedOwner)
                  const OwnerIcon = metadata.icon
                  return (
                    <CommandItem
                      isSelected={allowedOwner === owner}
                      key={allowedOwner}
                      startIcon={<OwnerIcon />}
                      value={allowedOwner}
                      onSelect={() => {
                        setOpen(false)
                        onOpenChange?.(false)
                        if (allowedOwner !== owner) {
                          setPendingOwner(allowedOwner)
                        }
                      }}
                    >
                      {metadata.title}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </ConfirmationDialog>
  )
}
